import { useDispatch, useSelector } from 'react-redux';

import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { combineReducers } from 'redux';

import { paymentStatementApi } from '../api/base';
import { getGlobalApiUrl, setGlobalApiUrl } from '../utils/globalConfig';

import authReducer from './authSlice';
import configReducer from './configSlice';
import onboardingReducer from './onboardingSlice';
import tenantReducer from './tenantSlice';

import type { ConfigureStoreOptions } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';

// Re-export the global API URL functions for backward compatibility
export { setGlobalApiUrl, getGlobalApiUrl };

const toastQueue: Array<{
	title: string;
	description: string;
	type: 'success' | 'error' | 'info';
	options?: { duration?: number };
}> = [];

const processToastQueue = (showToast: any) => {
	while (toastQueue.length > 0) {
		const toast = toastQueue.shift();
		if (toast) {
			showToast(toast.title, toast.description, toast.type, toast.options);
		}
	}
};

let toastContext: any = null;

export const setToastContext = (context: any) => {
	toastContext = context;
	if (context?.showToast) {
		processToastQueue(context.showToast);
	}
};

const toastMiddleware = (store: any) => (next: any) => (action: any) => {
	if (action.type === 'toast/showToast') {
		if (action.payload.type === 'error') {
			toastQueue.push(action.payload);
		}

		if (toastContext?.showToast) {
			toastContext.showToast(
				action.payload.title,
				action.payload.description,
				action.payload.type,
				action.payload.options,
			);
		}
	}
	return next(action);
};

const rootReducer = combineReducers({
	mfeAuth: authReducer,
	config: configReducer,
	onboarding: onboardingReducer,
	tenant: tenantReducer,
	[paymentStatementApi.reducerPath]: paymentStatementApi.reducer,
});

const createStore = (
	options?: ConfigureStoreOptions['preloadedState'] | undefined,
) =>
	configureStore({
		reducer: rootReducer,
		devTools: process.env.NODE_ENV === 'development',
		middleware: (getDefaultMiddleware) =>
			getDefaultMiddleware().concat(
				paymentStatementApi.middleware,
				toastMiddleware,
			),
		...options,
	});

export const store = createStore();
setupListeners(store.dispatch);

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export type RootState = ReturnType<typeof store.getState>;
export const useTypedSelector: TypedUseSelectorHook<RootState> = useSelector;
